import axios, {
	AxiosError,
	AxiosResponse,
	InternalAxiosRequestConfig
} from "axios";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/auth.store";
import { isTokenExpired, refreshAccessToken } from "@/utils/auth";
import { API_ENDPOINTS, resolveEndpoint } from "./endpoints";

type CustomInternalAxiosRequestConfig = InternalAxiosRequestConfig & {
	headers: Record<string, string>;
};

const baseURL = import.meta.env.VITE_API_URL;

const apiClient = axios.create({ baseURL });

const publicApiClient = axios.create({ baseURL });

apiClient.interceptors.request.use(
	async (config: CustomInternalAxiosRequestConfig) => {
		const token = useAuthStore.getState().accessToken;

		const isTokenExpire = isTokenExpired();

		if (token && isTokenExpire) {
			const newToken = await refreshAccessToken();
			if (newToken) {
				config.headers.Authorization = `Bearer ${newToken}`;
			} else {
				useAuthStore.getState().resetAuth();
			}
		} else if (!token) {
			useAuthStore.getState().resetAuth();
		} else {
			config.headers.Authorization = `Bearer ${token}`;
		}

		return config;
	},
	(error) => Promise.reject(error)
);

const handleAuthError = (error: AxiosError) => {
	const status = error.response?.status;
	const url = error.config?.url;
	const navigate = useNavigate();

	if (status && status >= 400 && status < 500 && url) {
		const loginEndpoint = resolveEndpoint(API_ENDPOINTS, ["AUTH", "LOGIN"]);

		const refreshEndpoint = resolveEndpoint(API_ENDPOINTS, ["AUTH", "REFRESH"]);

		if (url.includes(loginEndpoint) || url.includes(refreshEndpoint)) {
			useAuthStore.getState().resetAuth();

			navigate("/login", { replace: true });
		}
	}

	return Promise.reject(error);
};

apiClient.interceptors.response.use(
	(response: AxiosResponse) => response,
	handleAuthError
);

publicApiClient.interceptors.response.use(
	(response: AxiosResponse) => response,
	handleAuthError
);

export { apiClient, publicApiClient };
